import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Button,
  Modal,
  Box,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Collapse,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StripeCheckout from './StripeCheckout';
import SubscriptionManagement from './SubscriptionManagement';

export default function PricingCard({ plan }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const currentAccountType = useSelector((state) => state.auth.user.accountDetails.type);
  const currentPlanType = useSelector((state) => state.auth.user.accountDetails?.planType);
  const hasSpecialAccess = useSelector((state) => state.auth.user.accountDetails?.specialAccess);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const handleFreeTrialClick = () => {
    setShowCheckout(true);
  };

  const handleClose = () => {
    setShowCheckout(false);
  };
  
  const handleManagePlanClick = () => {
    setShowManagement(true);
  };
  
  const handleCloseManagement = () => {
    setShowManagement(false);
  };

  // Function to get appropriate icon based on feature content
  const getFeatureIcon = (feature) => {
    if (feature.includes('Coming Soon')) {
      return <AccessTimeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'orange.main' }} />;
    } else if (feature.includes('AI')) {
      return <SmartToyIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'primary.main' }} />;
    } else if (feature.includes('insights') || feature.includes('trends')) {
      return <TrendingUpIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'success.main' }} />;
    } else {
      return <CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'success.main' }} />;
    }
  };

  // Function to style feature text
  const getFeatureStyle = (feature) => {
    if (feature.includes('Coming Soon')) {
      return {
        color: 'text.secondary',
        fontStyle: 'italic',
        fontSize: { xs: 13, sm: 15 }
      };
    }
    return {
      fontSize: { xs: 13, sm: 15 },
      color: 'text.primary'
    };
  };

  // Check if plan has limited time pricing
  const hasLimitedOffer = plan.name === 'Premium' && plan.regularPrice && plan.limitedPrice && plan.regularPrice !== plan.limitedPrice;

  // Show limited features on mobile
  const featuresDisplay = isMobile && plan.list.length > 5 && !showAllFeatures 
    ? plan.list.slice(0, 5)
    : plan.list;

  const hasMoreFeatures = isMobile && plan.list.length > 5;

  return (
    <Card
      sx={{
        borderRadius: { xs: '8px', sm: '12px' },
        boxShadow: 'none',
        bgcolor: plan.name === 'Premium' ? 'common.white' : '#F8FAFC',
        position: 'relative',
        overflow: 'unset',
        boxShadow: plan.name === 'Premium' 
          ? 'rgba(99, 102, 241, 0.15) 0px 8px 32px' 
          : 'rgba(145, 158, 171, 0.08) 0px 2px 8px',
        border: plan.name === 'Premium' ? '2px solid' : '1px solid',
        borderColor: plan.name === 'Premium' ? 'primary.main' : 'grey.200',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: isMobile ? 'none' : 'translateY(-4px)',
          boxShadow: plan.name === 'Premium' 
            ? 'rgba(99, 102, 241, 0.25) 0px 12px 40px' 
            : 'rgba(145, 158, 171, 0.15) 0px 8px 24px',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3, md: '32px !important' } }}>
        {/* Mobile-optimized badges */}
        {plan.name === 'Premium' && (
          <Chip
            label="Most Popular"
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              left: { xs: 12, sm: 16 },
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: 10, sm: 12 },
              height: { xs: 20, sm: 24 }
            }}
          />
        )}

        {/* Inaugural Offer Badge */}
        {hasLimitedOffer && (
          <Chip
            icon={<FlashOnIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
            label="Inaugural"
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 16 },
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: 9, sm: 11 },
              height: { xs: 20, sm: 24 },
              boxShadow: 'rgba(99, 102, 241, 0.4) 0px 4px 12px',
            }}
          />
        )}

        <Stack spacing={1} sx={{ mt: plan.name === 'Premium' ? { xs: 3, sm: 1 } : 0 }}>
          <Typography 
            variant="h4" 
            fontWeight={900} 
            fontSize={{ xs: 24, sm: 28 }} 
            color="text.primary"
            sx={{ lineHeight: 1.2 }}>
            {plan.name}
          </Typography>
          
          {/* Mobile-optimized pricing display */}
          <Box sx={{ mb: 2 }}>
            {hasLimitedOffer ? (
              <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textDecoration: 'line-through', 
                    color: 'text.secondary',
                    fontSize: { xs: 16, sm: 18 },
                    fontWeight: 500
                  }}
                >
                  {plan.regularPrice}
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 900, 
                    color: 'error.main',
                    fontSize: { xs: 28, sm: 32, md: 40 },
                    lineHeight: 1
                  }}
                >
                  {plan.limitedPrice}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  /{plan.priceName === 'Yearly' ? 'year' : 'month'}
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 900, 
                    color: plan.name === 'Free' ? 'success.main' : 'primary.main',
                    fontSize: { xs: 28, sm: 32, md: 40 },
                    lineHeight: 1
                  }}
                >
                  {plan.price}
                </Typography>
                {plan.name !== 'Free' && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    /{plan.priceName === 'Yearly' ? 'year' : 'month'}
                  </Typography>
                )}
              </Stack>
            )}
            
            {/* Savings Text */}
            {hasLimitedOffer && plan.savingsText && (
              <Chip
                label={plan.savingsText}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: 'success.main',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: 10, sm: 12 },
                  height: { xs: 18, sm: 22 }
                }}
              />
            )}
          </Box>
          
          <Typography 
            variant="body1" 
            fontSize={{ xs: 13, sm: 15 }} 
            color="text.secondary" 
            sx={{ mb: 2, lineHeight: 1.4 }}>
            {plan.description}
          </Typography>

          {/* Premium plan highlight - Mobile optimized */}
          {plan.name === 'Premium' && (
            <Stack sx={{ 
              mb: 2, 
              p: { xs: 1.5, sm: 2 }, 
              bgcolor: 'primary.50', 
              borderRadius: { xs: 1.5, sm: 2 }
            }}>
              <Typography 
                variant="body2" 
                fontSize={{ xs: 11, sm: 13 }} 
                color="primary.main" 
                fontWeight={600}>
                {hasLimitedOffer ? 'INAUGURAL LAUNCH - FOUNDING MEMBERS!' : 'SAVE TIME & GROW FASTER'}
              </Typography>
              <Typography 
                variant="body2" 
                fontSize={{ xs: 11, sm: 13 }} 
                color="text.secondary"
                sx={{ lineHeight: 1.3 }}>
                {hasLimitedOffer 
                  ? `Join founding members: ${plan.limitedPrice} instead of ${plan.regularPrice}. Be part of the AI revolution!`
                  : 'AI automation saves 10+ hours/week with enterprise insights.'
                }
              </Typography>
            </Stack>
          )}

          {/* Features List - Mobile optimized */}
          <List
            sx={{
              px: 0,
              py: 1,
              '& .MuiListItem-root': { 
                px: 0, 
                py: { xs: 0.3, sm: 0.5 },
                alignItems: 'flex-start'
              },
            }}
          >
            {featuresDisplay.map((feature, index) => {
              const isComingSoon = feature.includes('Coming Soon');
              const cleanFeature = feature
                .replace(/^[✓•]\s*/, '') // Remove checkmark and bullet prefixes
                .replace(/\s*\(Coming Soon\)/, ''); // Remove Coming Soon text for display
              
              return (
                <ListItem key={index}>
                  <ListItemIcon
                    sx={{
                      minWidth: { xs: 24, sm: 28 },
                      mt: 0.5
                    }}
                  >
                    {getFeatureIcon(feature)}
                  </ListItemIcon>
                  <ListItemText>
                    <Typography sx={getFeatureStyle(feature)}>
                      {cleanFeature}
                      {isComingSoon && (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          variant="outlined"
                          sx={{
                            ml: 1,
                            height: { xs: 16, sm: 20 },
                            fontSize: { xs: 8, sm: 10 },
                            color: 'orange.main',
                            borderColor: 'orange.main'
                          }}
                        />
                      )}
                    </Typography>
                  </ListItemText>
                </ListItem>
              );
            })}
          </List>

          {/* Show more/less features button for mobile */}
          {hasMoreFeatures && (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              sx={{
                alignSelf: 'flex-start',
                fontSize: '0.8rem',
                textTransform: 'none',
                p: 0.5
              }}
              endIcon={showAllFeatures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showAllFeatures ? 'Show Less' : `Show ${plan.list.length - 5} More Features`}
            </Button>
          )}

          {/* Premium CTA section - Mobile optimized */}
          {plan.name === 'Premium' && (
            <Stack sx={{ 
              mt: 2, 
              p: { xs: 1.5, sm: 2 }, 
              bgcolor: 'success.50', 
              borderRadius: { xs: 1.5, sm: 2 }
            }}>
              <Typography 
                variant="body2" 
                fontSize={{ xs: 11, sm: 13 }} 
                color="success.main" 
                fontWeight={600}>
                PERFECT FOR:
              </Typography>
              <Typography 
                variant="body2" 
                fontSize={{ xs: 11, sm: 13 }} 
                color="text.secondary"
                sx={{ lineHeight: 1.3 }}>
                Growing businesses wanting AI automation and competitive advantages without enterprise costs.
              </Typography>
            </Stack>
          )}

          {/* Action Buttons - Mobile optimized */}
          <Box sx={{ mt: 3 }}>
            {currentAccountType === 'free' ? (
              plan.name === 'Free' ? (
                <Button
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    border: '1px solid', 
                    height: { xs: 44, sm: 48 },
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    boxShadow: 'none',
                    color: 'text.secondary',
                    cursor: 'default',
                    bgcolor: 'grey.100',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                  size="large"
                  disabled
                  fullWidth
                >
                  Your Current Plan
                </Button>
              ) : (
                <Button
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    height: { xs: 44, sm: 48 },
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    boxShadow: 'none',
                    background: hasLimitedOffer 
                      ? 'linear-gradient(135deg, #f44336 0%, #ff9800 100%)'
                      : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: hasLimitedOffer
                        ? 'linear-gradient(135deg, #d32f2f 0%, #f57c00 100%)'
                        : 'linear-gradient(135deg, #5856eb 0%, #7c3aed 100%)',
                      transform: isMobile ? 'none' : 'translateY(-1px)',
                    },
                  }}
                  size="large"
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleFreeTrialClick}
                >
                  {hasLimitedOffer ? 'Join as Founding Member' : 'Upgrade to Premium'}
                </Button>
              )
            ) : currentAccountType === 'premium' ? (
              plan.name === 'Premium' ? (
                <Stack spacing={1}>
                  <Typography
                    variant="body2"
                    color="primary"
                    textAlign="center"
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      flexWrap: 'wrap'
                    }}
                  >
                    <span>You're on the {currentPlanType || 'Premium'} plan</span>
                    {hasSpecialAccess && (
                      <Chip
                        label="Special Access"
                        size="small"
                        icon={<StarIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                        sx={{
                          bgcolor: 'success.main',
                          color: 'white',
                          fontSize: { xs: 8, sm: 10 },
                          height: { xs: 18, sm: 20 },
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                  </Typography>
                  
                  {/* Show different buttons based on special access */}
                  {!hasSpecialAccess ? (
                    <Button
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        height: { xs: 44, sm: 48 },
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        boxShadow: 'none',
                      }}
                      size="large"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      startIcon={<SettingsIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                      onClick={handleManagePlanClick}
                    >
                      Manage Subscription
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: 'success.50',
                        borderRadius: { xs: 1.5, sm: 2 },
                        border: '1px solid',
                        borderColor: 'success.200',
                        textAlign: 'center'
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ 
                          fontWeight: 600, 
                          mb: 0.5,
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}
                      >
                        Beta Access Account
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: 11, sm: 13 } }}
                      >
                        Contact support for account changes
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ 
                    fontWeight: 500, 
                    mt: 2,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <span>You're currently on Premium</span>
                  {hasSpecialAccess && (
                    <Chip
                      label="Special Access"
                      size="small"
                      sx={{
                        bgcolor: 'success.main',
                        color: 'white',
                        fontSize: { xs: 8, sm: 10 },
                        height: { xs: 16, sm: 18 }
                      }}
                    />
                  )}
                </Typography>
              )
            ) : null}
          </Box>
        </Stack>
      </CardContent>

      {/* Modal for Stripe Checkout - Mobile optimized */}
      <Modal
        open={showCheckout}
        onClose={handleClose}
        closeAfterTransition
        aria-labelledby="checkout-modal-title"
      >
        <Fade in={showCheckout}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 600 },
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: { xs: 2, sm: 2 },
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: { xs: 1.5, sm: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                bgcolor: 'background.paper',
                zIndex: 1200
              }}
            >
              <Typography 
                id="checkout-modal-title" 
                variant="h6" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {hasLimitedOffer ? 'Join as Founding Member' : 'Complete Premium Upgrade'}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{ color: 'grey.500' }}
                size={isMobile ? 'small' : 'medium'}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box
              sx={{
                overflowY: 'auto',
                flex: 1,
                px: { xs: 1.5, sm: 2 },
                py: 0,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
                scrollbarWidth: 'thin',
              }}
            >
              <StripeCheckout plan={plan}/>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Modal for Subscription Management - Mobile optimized */}
      {!hasSpecialAccess && (
        <Modal
          open={showManagement}
          onClose={handleCloseManagement}
          closeAfterTransition
          aria-labelledby="management-modal-title"
        >
          <Fade in={showManagement}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: 600 },
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: { xs: 2, sm: 2 },
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                outline: 'none',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: { xs: 1.5, sm: 2 },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1200
                }}
              >
                <Typography 
                  id="management-modal-title" 
                  variant="h6" 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Manage Subscription
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseManagement}
                  sx={{ color: 'grey.500' }}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  p: { xs: 1.5, sm: 2 },
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                  },
                  scrollbarWidth: 'thin',
                }}
              >
                <SubscriptionManagement onClose={handleCloseManagement} />
              </Box>
            </Box>
          </Fade>
        </Modal>
      )}
    </Card>
  );
}